var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { isEmptyAnswer } from './types';
import { ActionBar } from './action-bar';
import { useNavigate, useParams } from 'react-router-dom';
import Answer from './answer';
import ErrorDialog from './error-dialog';
import '../../less/grading.less';
import AnswerControls from './answer-controls';
import ScoreHistory from './score-history';
import { GradingScoreTable, PregradingScoreTable } from './score-table';
import { AnswerComment } from './answer-comment';
import classNames from 'classnames';
import ScoreTableInstructions from './score-table-instructions';
import { HelpDialog } from './help-dialog';
import { getJson, postJson } from '../common/utils';
import { StudentRowActions } from './student-row-actions';
import { MarkPregradingsReadyUI } from './MarkPregradingsFinishedUI';
import { add, isBefore } from 'date-fns';
import { PregradingControls } from './PregradingControls';
import { AnswerSearch } from './answer-search';
import Split from 'react-split';
import { useNavigateInGrid } from './grid-navigation';
import { postScoreUpdateState } from './post-score-utils';
import { I18nextProvider, useTranslation } from 'react-i18next';
import i18next from '../locales/i18n';
export const GradingContext = React.createContext({});
export const GradingUrlsContext = React.createContext({});
export const hasCensorWaitingPeriodPassedForAnswer = (answer, waitingHours) => {
    var _a, _b;
    return ((_a = answer === null || answer === void 0 ? void 0 : answer.pregrading) === null || _a === void 0 ? void 0 : _a.pregradingFinishedAt) && waitingHours
        ? isBefore(add(new Date((_b = answer === null || answer === void 0 ? void 0 : answer.pregrading) === null || _b === void 0 ? void 0 : _b.pregradingFinishedAt), { hours: waitingHours }), new Date())
        : false;
};
function CommonGradingView(props) {
    var _a, _b, _c, _d, _e, _f;
    const { gradingRole, ScoreTable, waitingForCensorHours } = props;
    const { schoolExamAnonCode, studentCode, displayNumber } = useParams();
    const navigate = useNavigate();
    const gradingUrls = useContext(GradingUrlsContext);
    const { t } = useTranslation();
    const allExams = JSON.parse(window.localStorage.getItem('allExams') || '[]');
    const [examAndScores, setExamAndScores] = useState();
    const [userData, setUserData] = useState();
    const [showPregradingAnnotations, setShowPregradingAnnotations] = useState(gradingRole === 'pregrading' || localStorage.getItem('hide-annotations') === null);
    const [showAllPregradingScores, setShowAllPregradingScores] = useState(false);
    const [localizedError, setLocalizedError] = useState();
    const [helpVisible, setHelpVisible] = useState(false);
    const [loading, setLoading] = useState(true);
    const [cellSaving, setCellSaving] = useState(false);
    const [studentEdited, setStudentEdited] = React.useState();
    const [showMarkGradingFinishedUI, setShowMarkGradingFinishedUI] = useState(false);
    const [showAnswerSearchUI, setShowAnswerSearchUI] = useState(false);
    const [markOnlyMyGradesFinished, setMarkOnlyMyGradesFinished] = useState(true);
    const [markOnlySelectedGradesFinished, setMarkOnlySelectedGradesFinished] = useState(false);
    const [columnsToMarkFinished, setColumnsToMarkFinished] = useState([]);
    const [rowsToMarkFinished, setRowsToMarkFinished] = useState([]);
    const exam = examAndScores === null || examAndScores === void 0 ? void 0 : examAndScores.exam;
    const DEFAULT_SPLIT_SIZES = [75, 25];
    const [latestSplitSizes, setLatestSplitSizes] = useState(DEFAULT_SPLIT_SIZES);
    const setUpdating = useCallback((updating) => {
        setExamAndScores((previousState) => {
            if (!previousState)
                return previousState;
            return Object.assign(Object.assign({}, previousState), { updating });
        });
    }, [setExamAndScores]);
    useEffect(() => {
        const savedSizes = localStorage.getItem('split-sizes');
        if (savedSizes) {
            const parsed = JSON.parse(savedSizes);
            if (parsed.length === 2 && parsed.every(size => typeof size === 'number')) {
                setLatestSplitSizes(parsed);
            }
            else {
                setLatestSplitSizes(DEFAULT_SPLIT_SIZES);
            }
        }
    }, [showAnswerSearchUI]);
    useEffect(() => {
        setMarkOnlySelectedGradesFinished(columnsToMarkFinished.length + rowsToMarkFinished.length > 0);
    }, [columnsToMarkFinished, rowsToMarkFinished]);
    useEffect(() => {
        if (helpVisible) {
            document.addEventListener('keydown', handleKeyDown);
        }
        else {
            document.removeEventListener('keydown', handleKeyDown);
        }
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [helpVisible]);
    function navigateToNextSchool(delta) {
        const newIndex = allExams.findIndex(exam => exam.schoolAnonCode == schoolExamAnonCode) + delta;
        const newIndexWithinRange = newIndex < 0 ? -1 : newIndex >= allExams.length ? 0 : newIndex;
        const nextSchool = allExams.at(newIndexWithinRange);
        if (nextSchool) {
            navigate(gradingUrls.navigateToSchool(gradingRole, nextSchool.schoolAnonCode));
        }
    }
    const isImpersonating = (userData) => { var _a; return ((_a = userData === null || userData === void 0 ? void 0 : userData.user) === null || _a === void 0 ? void 0 : _a.details.impersonation) !== undefined; };
    useEffect(() => {
        let isCancel = false;
        const fetchDataAsync = () => __awaiter(this, void 0, void 0, function* () {
            if (!userData) {
                const userRes = yield getJson(gradingUrls.getUser());
                if (userRes.json) {
                    return setUserData(userRes.json);
                }
            }
            const examAndScoresRes = yield getJson(gradingUrls.getExamAndScores(gradingRole, schoolExamAnonCode));
            setLoading(false);
            const examAndScoresJson = examAndScoresRes.json;
            if (examAndScoresJson) {
                if (gradingRole === 'censoring' && examAndScoresJson.exam.censorDistributionDone) {
                    examAndScoresJson.exam.isCensoringEnabled =
                        examAndScoresJson.exam.isCensoringEnabled || isImpersonating(userData);
                }
                examAndScoresJson.updating = false;
                if (!isCancel)
                    setExamAndScores(examAndScoresJson);
            }
            if (examAndScoresRes.status >= 400) {
                if (examAndScoresRes.status == 404) {
                    setLocalizedError({
                        localizeKey: 'arpa.errors.exam_not_found',
                        options: {
                            close: () => window.location.assign(gradingUrls.returnToEvents(gradingRole)),
                            showReload: false
                        }
                    });
                }
                else {
                    if (!isCancel)
                        setLocalizedError({ localizeKey: 'arpa.errors.problem', options: { logoutButton: true } });
                }
            }
        });
        try {
            void fetchDataAsync();
        }
        catch (e) {
            if (!isCancel)
                setLocalizedError({ localizeKey: 'arpa.errors.problem', options: { logoutButton: true } });
        }
        return () => {
            isCancel = true;
        };
    }, [schoolExamAnonCode, userData]);
    const isScoring = studentCode && displayNumber;
    const student = (_a = examAndScores === null || examAndScores === void 0 ? void 0 : examAndScores.students) === null || _a === void 0 ? void 0 : _a.find(student => (student === null || student === void 0 ? void 0 : student.studentAnonIdentifier) === Number(studentCode));
    const answer = student === null || student === void 0 ? void 0 : student.answers.find((answer) => answer.displayNumber == displayNumber);
    const allStudentsVisible = ((examAndScores === null || examAndScores === void 0 ? void 0 : examAndScores.students) || []).every(student => !student.isHidden);
    useEffect(() => {
        const displayNumberPart = displayNumber ? `${displayNumber}.` : '';
        const title = exam ? exam.content.title.substring(5) : '';
        const studentPart = schoolExamAnonCode && studentCode ? ` (${studentCode} / ${schoolExamAnonCode})` : '';
        document.title = `${displayNumberPart}${studentPart} ${title}`;
        if (!isScoring) {
            document.body.classList.remove('full_screen');
        }
    }, [answer === null || answer === void 0 ? void 0 : answer.answerId]);
    const unfinishedStudentsAndAnswers = useMemo(() => examAndScores === null || examAndScores === void 0 ? void 0 : examAndScores.students.reduce((acc, student) => {
        student.answers.forEach(answer => {
            var _a, _b, _c, _d;
            if (!((_a = answer.pregrading) === null || _a === void 0 ? void 0 : _a.pregradingFinishedAt) && ((_b = answer.pregrading) === null || _b === void 0 ? void 0 : _b.scoreValue) != null) {
                const answerData = {
                    id: answer.answerId,
                    displayNumber: answer.displayNumber,
                    studentUuid: student.studentUuid,
                    pregradedByUser: ((_c = answer.pregrading) === null || _c === void 0 ? void 0 : _c.authorId) === ((_d = userData === null || userData === void 0 ? void 0 : userData.user) === null || _d === void 0 ? void 0 : _d.userAccountId)
                };
                acc.push(answerData);
            }
        });
        return acc;
    }, []), [examAndScores]);
    const navigateToAnswer = useCallback((newStudentCode, newDisplayNumber, newSchoolExamCode) => {
        if (newStudentCode === undefined || newDisplayNumber === undefined) {
            return;
        }
        navigate(gradingUrls.navigateToAnswer(gradingRole, newSchoolExamCode || schoolExamAnonCode, newStudentCode.toString(), newDisplayNumber), {
            replace: true
        });
    }, [schoolExamAnonCode, gradingRole]);
    const navigateInGrid = useNavigateInGrid(examAndScores, gradingRole, gradingUrls, setUpdating, navigateToAnswer);
    if (loading) {
        return React.createElement("div", { style: { padding: '1rem' } }, t('sa.censor.loading'));
    }
    function updateState(pregradingResponse) {
        // Create a lookup object where the keys are studentUuid and the values are another object
        // where the keys are answerId and the values are the corresponding pregrading object
        const data = {};
        pregradingResponse.forEach(pregrading => {
            if (!data[pregrading.studentUuid]) {
                data[pregrading.studentUuid] = {};
            }
            data[pregrading.studentUuid][pregrading.answerId] = pregrading;
        });
        const newExamAndScores = Object.assign(Object.assign({}, examAndScores), { students: examAndScores === null || examAndScores === void 0 ? void 0 : examAndScores.students.map(student => (Object.assign(Object.assign({}, student), { answers: student.answers.map(answer => {
                    var _a, _b, _c, _d, _e, _f;
                    return (Object.assign(Object.assign({}, answer), { userCanScore: (_c = (_b = (_a = data[student.studentUuid]) === null || _a === void 0 ? void 0 : _a[answer.answerId]) === null || _b === void 0 ? void 0 : _b.userCanScore) !== null && _c !== void 0 ? _c : answer.userCanScore, pregrading: ((_d = data[student.studentUuid]) === null || _d === void 0 ? void 0 : _d[answer.answerId])
                            ? Object.assign(Object.assign({}, (_e = data[student.studentUuid]) === null || _e === void 0 ? void 0 : _e[answer.answerId]), { metadata: (_f = answer.pregrading) === null || _f === void 0 ? void 0 : _f.metadata }) : answer.pregrading }));
                }) }))) });
        setExamAndScores((previousState) => {
            if (!previousState)
                return previousState;
            return newExamAndScores;
        });
    }
    const finishPregradingForMultipleAnswers = (ids) => __awaiter(this, void 0, void 0, function* () {
        const response = yield postMarkPregradingFinished(ids);
        if (markOnlySelectedGradesFinished) {
            setColumnsToMarkFinished([]);
            setRowsToMarkFinished([]);
        }
        return response;
    });
    function markCensorWaitingTimePassedForMultipleAnswers(answerIds) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield postSetCensorWaitingPeriodPassed(answerIds);
            if (markOnlySelectedGradesFinished) {
                setColumnsToMarkFinished([]);
                setRowsToMarkFinished([]);
            }
            return response;
        });
    }
    const postMarkPregradingFinished = (answerIds) => __awaiter(this, void 0, void 0, function* () {
        if ((answerIds === null || answerIds === void 0 ? void 0 : answerIds.length) === 0) {
            return;
        }
        const response = yield postJson(gradingUrls.markPregradingFinished(schoolExamAnonCode), { answerIds });
        if (response.status >= 400) {
            setLocalizedError({ localizeKey: 'arpa.errors.problem' });
            return;
        }
        if (response.json) {
            updateState(response.json);
            return response.json;
        }
    });
    function postSetCensorWaitingPeriodPassed(answerIds) {
        return __awaiter(this, void 0, void 0, function* () {
            if ((answerIds === null || answerIds === void 0 ? void 0 : answerIds.length) === 0) {
                return;
            }
            const response = yield postJson(`/dev/answers/set-censor-waiting-period-passed`, { answerIds });
            if (response.json) {
                updateState(response.json);
                return response.json;
            }
        });
    }
    function postRevertPregradingFinishedAt(answerId) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield postJson(gradingUrls.revertPregradingFinished(answerId));
            if (response.json) {
                updateState(response.json);
                return;
            }
        });
    }
    const pregradingDone = (answer === null || answer === void 0 ? void 0 : answer.pregrading) && answer.pregrading.pregradingFinishedAt;
    const pregradingAnnotationsEnabled = (_b = ((exam === null || exam === void 0 ? void 0 : exam.canBePregraded) && !(student === null || student === void 0 ? void 0 : student.skipPregrading) && !pregradingDone)) !== null && _b !== void 0 ? _b : false;
    const gradingAnnotationsEnabled = (_c = ((exam === null || exam === void 0 ? void 0 : exam.isCensoringEnabled) && !!exam.censorDistributionDone)) !== null && _c !== void 0 ? _c : false;
    const questionIds = gradingRole == 'censoring' && exam ? exam.content.sections.flatMap(section => section.questions) : [];
    function postScoreAndUpdateState(answer, scoreValue, setCellSavingState) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                if (isEmptyAnswer(answer)) {
                    return;
                }
                setCellSaving(true);
                const scoreValueNumber = scoreValue == '' ? null : Number(scoreValue);
                setCellSavingState(true);
                const scoreResponse = yield postJson(gradingUrls.postScore(gradingRole, answer.answerId), {
                    scoreValue: scoreValueNumber,
                    versionNumber: answer.versionNumber + 1
                });
                setCellSavingState(false);
                if (scoreResponse.json) {
                    if (scoreValueNumber !== scoreResponse.json.scoreValue) {
                        setLocalizedError({ localizeKey: 'arpa.errors.saving_score_failed' });
                    }
                    const shouldUpdateCensoringState = gradingRole === 'censoring';
                    postScoreUpdateState(setExamAndScores, scoreResponse.json, answer.questionId, shouldUpdateCensoringState, scoreValueNumber);
                }
                setCellSaving(false);
                if (scoreResponse.status >= 400) {
                    if (scoreResponse.status == 401) {
                        window.location.assign('/');
                    }
                    else {
                        setLocalizedError({ localizeKey: 'arpa.errors.saving_score_failed' });
                    }
                }
            }
            catch (err) {
                setLocalizedError({ localizeKey: 'arpa.errors.saving_score_failed' });
            }
        });
    }
    const getAnswerCommentInput = (answer, exam) => (React.createElement(AnswerComment, { gradingRole: gradingRole, answer: answer, setExamAndScores: setExamAndScores, setLocalizedError: setLocalizedError, canBeCommented: gradingRole === 'censoring' ? exam.canBeCommented : !!exam.canBePregraded, postCommentUrl: gradingUrls.postComment(gradingRole, answer.answerId) }));
    return (React.createElement(GradingContext.Provider, { value: {
            user: userData,
            navigateInGrid,
            currentAnswer: {
                schoolExamAnonCode,
                studentCode,
                displayNumber
            },
            unfinishedStudentsAndAnswers,
            columnsToMarkFinished,
            rowsToMarkFinished,
            markOnlyMyGradesFinished,
            markOnlySelectedGradesFinished,
            showAllPregradingScores,
            postScoreAndUpdateState,
            navigateToAnswer,
            waitingForCensorHours: waitingForCensorHours
        } },
        !isScoring && props.PageBanner,
        exam && (React.createElement("div", { id: "fixed-pos-wrapper", className: classNames({ is_scoring: isScoring }) },
            React.createElement("div", { className: "top-bar" },
                React.createElement(ActionBar, { exam: exam, gradingRole: gradingRole, setShowPregradingAnnotations: setShowPregradingAnnotations, setShowAllPregradingScores: setShowAllPregradingScores, setHelpVisible: setHelpVisible, schoolExamAnonCode: schoolExamAnonCode, studentCode: studentCode, displayNumber: displayNumber, navigate: navigate, setShowAnswerSearchUI: setShowAnswerSearchUI, showAnswerSearchUI: showAnswerSearchUI, examContentUrl: gradingUrls.examContent(exam.examUuid, exam.examCode, exam.eventDate, exam.language, exam.examType), attachmentContentUrl: gradingUrls.attachmentsContent(exam.examUuid, exam.examCode, exam.eventDate, exam.language, exam.examType), gradingInstructionsUrl: gradingUrls.gradingInstructions(exam.examUuid, exam.examCode, exam.eventDate, exam.language, exam.examType), returnToGridUrl: gradingUrls.returnToGrid(gradingRole, schoolExamAnonCode, exam.examCode), returnToEventsUrl: gradingUrls.returnToEvents(gradingRole) }),
                gradingRole === 'pregrading' && !answer && (React.createElement("div", { id: "toggle-show-grading-ui" },
                    React.createElement("button", { onClick: () => setShowMarkGradingFinishedUI(true), className: "button" },
                        t('sa.pregrading.mark_gradings_finished'),
                        "...")))),
            React.createElement(Split, { sizes: showAnswerSearchUI ? latestSplitSizes : [100, 0], minSize: [100, showAnswerSearchUI ? 300 : 0], className: "split-view", expandToMin: true, snapOffset: 0, cursor: "col-resize", gutterSize: 10, gutterStyle: () => ({ display: showAnswerSearchUI ? 'block' : 'none' }), onDragEnd: sizes => localStorage.setItem('split-sizes', JSON.stringify(sizes)) },
                React.createElement("main", null,
                    !isScoring && (React.createElement("div", { className: "arpa-overview-exam-info" },
                        React.createElement("h1", { className: "exam-name" },
                            exam.schoolAnonCode,
                            " ",
                            exam.content.title),
                        React.createElement(ScoreTableInstructions, { schoolExamAnonCode: schoolExamAnonCode !== null && schoolExamAnonCode !== void 0 ? schoolExamAnonCode : '', canBeGraded: gradingRole === 'pregrading' ? !!exam.canBePregraded : exam.isCensoringEnabled, students: examAndScores.students, allStudentsVisible: allStudentsVisible, gradingRole: gradingRole }))),
                    studentEdited && (React.createElement(StudentRowActions, { examAndScores: examAndScores, setExamAndScores: setExamAndScores, student: examAndScores.students.find(s => s.studentUuid == studentEdited), setStudentEdited: setStudentEdited, setLocalizedError: setLocalizedError })),
                    React.createElement(ScoreTable, { examAndScores: examAndScores, navigateToNextSchool: navigateToNextSchool, setExamAndScores: setExamAndScores, gradingRole: gradingRole, student: student, studentEdited: studentEdited, setStudentEdited: setStudentEdited, showMarkGradingFinishedUI: showMarkGradingFinishedUI, setColumnsToMarkFinished: setColumnsToMarkFinished, setRowsToMarkFinished: setRowsToMarkFinished }),
                    React.createElement("div", { key: answer === null || answer === void 0 ? void 0 : answer.answerId, className: "answer-and-scores", tabIndex: 1 }, answer && (React.createElement(React.Fragment, null,
                        React.createElement("div", { className: "score-margin" },
                            React.createElement(ScoreHistory, { answer: answer, gradingRole: gradingRole, skipPregrading: (_d = student === null || student === void 0 ? void 0 : student.skipPregrading) !== null && _d !== void 0 ? _d : false }),
                            React.createElement(AnswerControls, { examAndScores: examAndScores, setExamAndScores: setExamAndScores, setLocalizedError: setLocalizedError, gradingRole: gradingRole }),
                            gradingRole === 'pregrading' && (React.createElement(PregradingControls, { answer: answer, isImpersonating: isImpersonating(userData), isPregradedByUser: ((_e = answer.pregrading) === null || _e === void 0 ? void 0 : _e.authorId) === ((_f = userData === null || userData === void 0 ? void 0 : userData.user) === null || _f === void 0 ? void 0 : _f.userAccountId), cellSavingOngoing: cellSaving, postRevertPregradingFinishedAt: postRevertPregradingFinishedAt, postMarkPregradingFinished: postMarkPregradingFinished }))),
                        React.createElement(Answer, { answer: answer, gradingRole: gradingRole, setLocalizedError: setLocalizedError, setExamAndScores: setExamAndScores, AnswerCommentElement: getAnswerCommentInput(answer, exam), canBeAnnotated: pregradingAnnotationsEnabled || gradingAnnotationsEnabled, showPregradingAnnotations: showPregradingAnnotations }))))),
                React.createElement("div", { className: "split-view-right-column" }, gradingRole === 'censoring' && showAnswerSearchUI && (React.createElement("aside", { id: "answer-search", className: showAnswerSearchUI ? 'visible' : '' },
                    React.createElement(AnswerSearch, { maxScores: examAndScores.maxScores, displayNumber: displayNumber, examUuid: exam.examUuid, examDetails: exam, questionIds: questionIds, setExamAndScores: setExamAndScores, examAndScores: examAndScores, setLocalizedError: setLocalizedError }))))),
            gradingRole === 'pregrading' && !answer && (React.createElement("aside", { id: "mark-grading-finished", className: showMarkGradingFinishedUI ? 'visible' : '' },
                React.createElement(MarkPregradingsReadyUI, { userData: userData, markCensorWaitingTimePassedForMultipleAnswers: markCensorWaitingTimePassedForMultipleAnswers, finishPregradingForMultipleAnswers: finishPregradingForMultipleAnswers, closeView: () => setShowMarkGradingFinishedUI(false), markOnlyMyGradesFinished: markOnlyMyGradesFinished, setMarkOnlyMyGradesFinished: setMarkOnlyMyGradesFinished, setMarkOnlySelectedGradesFinished: setMarkOnlySelectedGradesFinished }))))),
        localizedError && (React.createElement(ErrorDialog, { err: { localizeKey: localizedError.localizeKey, options: Object.assign({ showReload: true }, localizedError.options) } })),
        helpVisible && React.createElement(HelpDialog, { setHelpVisible: setHelpVisible })));
    function handleKeyDown(event) {
        if (event.key === 'Escape') {
            setHelpVisible(false);
        }
    }
}
export function PregradingView({ pregradingUrls, waitingForCensorHours, PageBanner, lang }) {
    return (React.createElement(I18nextProvider, { i18n: i18next(lang) },
        React.createElement(GradingUrlsContext.Provider, { value: pregradingUrls },
            React.createElement(CommonGradingView, { gradingUrls: pregradingUrls, gradingRole: "pregrading", ScoreTable: PregradingScoreTable, waitingForCensorHours: waitingForCensorHours, PageBanner: PageBanner }))));
}
export function GradingView({ gradingUrls, waitingForCensorHours, PageBanner, lang }) {
    return (React.createElement(I18nextProvider, { i18n: i18next(lang) },
        React.createElement(GradingUrlsContext.Provider, { value: gradingUrls },
            React.createElement(CommonGradingView, { gradingUrls: gradingUrls, gradingRole: "censoring", ScoreTable: GradingScoreTable, waitingForCensorHours: waitingForCensorHours, PageBanner: PageBanner }))));
}
//# sourceMappingURL=grading-view.js.map